import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { LogOut, Download, Menu, X, Shield, MessageSquare, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import FeedbackModal from "@/components/modals/FeedbackModal";

interface NavItemProps {
  href: string;
  label: string;
  isActive?: boolean;
  disabled?: boolean;
  badge?: string;
}

function NavItem({ href, label, isActive, disabled = false, badge }: NavItemProps) {
  const content = (
    <div
      className={`group relative flex items-center gap-2 px-5 py-2 text-sm font-semibold transition-all duration-200 cursor-pointer ${
        disabled
          ? "text-muted-foreground/50 cursor-not-allowed"
          : isActive
          ? "text-foreground"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <span className="tracking-wide">{label}</span>
      {badge && (
        <span className="px-1.5 py-0.5 text-[10px] font-medium leading-none rounded-full bg-primary/10 text-primary border border-primary/20">
          {badge}
        </span>
      )}
      {/* Underline */}
      {!disabled && (
        <span
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-primary rounded-full transition-all duration-200 ${
            isActive ? "w-3/4" : "w-0 group-hover:w-3/4"
          }`}
        />
      )}
    </div>
  );

  if (disabled) {
    return <div className="cursor-not-allowed">{content}</div>;
  }

  return <Link to={href}>{content}</Link>;
}

/**
 * Get initials from display name or email.
 */
function getInitials(displayName?: string, email?: string): string {
  // Try display name first
  if (displayName) {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
  }
  // Fall back to email
  if (!email) return "??";
  const localPart = email.split("@")[0];
  const parts = localPart.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return localPart.slice(0, 2).toUpperCase();
}

/**
 * Get first name from display name or email.
 */
function getFirstName(displayName?: string, email?: string): string {
  // Try display name first
  if (displayName) {
    const parts = displayName.trim().split(/\s+/);
    return parts[0];
  }
  // Fall back to email
  if (!email) return "User";
  const localPart = email.split("@")[0];
  const parts = localPart.split(/[._-]/);
  return parts[0];
}

/**
 * Header navigation component with horizontal navigation items.
 */
export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const displayName = user?.display_name || user?.email || "User";
  const firstName = getFirstName(user?.display_name, user?.email);
  const initials = getInitials(user?.display_name, user?.email);
  const isAdmin = user?.role === "admin";
  const roleDisplay = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "";

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isProfilePopupOpen &&
        profileRef.current &&
        popupRef.current &&
        !profileRef.current.contains(event.target as Node) &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setIsProfilePopupOpen(false);
      }
    };

    if (isProfilePopupOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfilePopupOpen]);

  const [isSkillDropdownOpen, setIsSkillDropdownOpen] = useState(false);
  const skillDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(event.target as Node)) {
        setIsSkillDropdownOpen(false);
      }
    }
    if (isSkillDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSkillDropdownOpen]);

  const skillPlatforms = [
    { label: "Claude.ai / Desktop", file: "/skills/claude-desktop-skill.zip" },
    { label: "Claude Code", file: "/skills/claude-code-skill.zip" },
    { label: "OpenAI / Codex", file: "/skills/openai-skill.zip" },
  ];

  const handleDownloadPlatform = (file: string) => {
    const link = document.createElement("a");
    link.href = file;
    link.download = file.split("/").pop() || "skill.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsSkillDropdownOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-50 h-20 bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm">
      <div className="h-full flex items-center px-4 sm:px-8 max-w-[1920px] mx-auto">
        {/* Logo/Brand */}
        <div className="flex items-center">
          <Link to="/" className="relative group cursor-pointer">
            <img 
              src="/logo-no-text.png" 
              alt="Feedforward AI" 
              className="h-10 w-auto transition-transform duration-300 group-hover:scale-105 md:hidden"
            />
            <img 
              src="/logo.png" 
              alt="Feedforward AI" 
              className="h-10 w-auto transition-transform duration-300 group-hover:scale-105 hidden md:block"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300 -z-10" />
          </Link>
        </div>

        {/* Right side: Navigation + User profile */}
        <div className="ml-auto flex items-center gap-4 sm:gap-6">
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center h-full gap-2">
            <NavItem
              href="/analysis"
              label="Evaluate"
              isActive={location.pathname === "/analysis"}
            />
            <NavItem
              href="/vendor-call"
              label="Vendor Call"
              isActive={location.pathname === "/vendor-call"}
            />
            {isAdmin && (
              <NavItem
                href="/chat"
                label="Chat"
                isActive={location.pathname === "/chat"}
              />
            )}
            <NavItem
              href="/transparency"
              label="How It Works"
              isActive={location.pathname === "/transparency"}
            />
          </nav>

          {/* Download Skill dropdown */}
          <div className="hidden md:block relative" ref={skillDropdownRef}>
              <Button
                size="sm"
                onClick={() => setIsSkillDropdownOpen(!isSkillDropdownOpen)}
                className="flex gap-2 !bg-[#d4975c] !text-white hover:!bg-[#c48a50] transition-all duration-300"
              >
                <Download className="w-4 h-4" />
                Download Skill
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isSkillDropdownOpen ? "rotate-180" : ""}`} />
              </Button>
              {isSkillDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg border border-border bg-background shadow-xl z-50 py-1 animate-in fade-in slide-in-from-top-2 duration-200">
                  {skillPlatforms.map((platform) => (
                    <button
                      key={platform.file}
                      onClick={() => handleDownloadPlatform(platform.file)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                    >
                      <Download className="w-3.5 h-3.5 text-muted-foreground" />
                      {platform.label}
                    </button>
                  ))}
                </div>
              )}
          </div>

          {/* User profile */}
          <div className="relative isolate">
            <div
              ref={profileRef}
              onClick={() => setIsProfilePopupOpen(!isProfilePopupOpen)}
              className={`flex items-center gap-3 cursor-pointer rounded-xl p-2 pr-3 sm:pr-4 transition-all duration-300 ${
                isProfilePopupOpen
                  ? "bg-muted shadow-md scale-105"
                  : "hover:bg-muted/50 hover:shadow-sm"
              }`}
            >
              <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary text-sm font-semibold text-primary-foreground shadow-md ring-2 ring-background transition-transform duration-300 hover:scale-110">
                {initials}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/50 to-secondary/50 opacity-0 hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="text-sm hidden sm:block">
                <div className="font-semibold text-foreground leading-tight truncate max-w-[150px]">
                  {firstName}
                </div>
              </div>
            </div>

            {/* Profile Popup */}
            {isProfilePopupOpen && (
              <div
                ref={popupRef}
                className="fixed top-20 right-4 sm:right-8 w-[calc(100vw-2rem)] sm:w-64 max-w-64 bg-card border border-border rounded-xl shadow-2xl z-[9999] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
              >
                <div className="p-2">
                  <div className="px-3 py-2 mb-1 border-b border-border/50">
                    <div className="font-semibold text-foreground text-sm truncate">{displayName}</div>
                    {user?.display_name && user?.email && (
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-3 text-foreground hover:bg-muted/50 transition-all duration-200"
                      onClick={() => {
                        setIsProfilePopupOpen(false);
                        navigate("/admin/users");
                      }}
                    >
                      <Shield className="h-4 w-4" />
                      <span className="font-medium">Admin</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 text-foreground hover:bg-muted/50 transition-all duration-200"
                    onClick={() => {
                      setIsProfilePopupOpen(false);
                      setIsFeedbackModalOpen(true);
                    }}
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span className="font-medium">Feedback</span>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="font-medium">Logout</span>
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile hamburger button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-11 h-11 rounded-lg hover:bg-muted/50 transition-colors"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer - fixed to avoid being clipped by parent overflow */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop - covers content area below header, tap to close */}
          <div
            className="md:hidden fixed top-20 left-0 right-0 bottom-0 z-[45] bg-black/40"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer panel - fully opaque so content doesn't show through */}
          <div className="md:hidden fixed top-20 left-0 right-0 z-[50] bg-background border-b border-border shadow-xl">
            <nav className="flex flex-col p-4 gap-1">
            <Link
              to="/analysis"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`relative px-4 py-3 text-sm font-semibold transition-all duration-200 border-b-2 ${
                location.pathname === "/analysis"
                  ? "text-foreground border-primary"
                  : "text-muted-foreground hover:text-foreground border-transparent hover:border-primary"
              }`}
            >
              Evaluate
            </Link>
            <Link
              to="/vendor-call"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`relative px-4 py-3 text-sm font-semibold transition-all duration-200 border-b-2 ${
                location.pathname === "/vendor-call"
                  ? "text-foreground border-primary"
                  : "text-muted-foreground hover:text-foreground border-transparent hover:border-primary"
              }`}
            >
              Vendor Call
            </Link>
            {isAdmin && (
              <Link
                to="/chat"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`relative px-4 py-3 text-sm font-semibold transition-all duration-200 border-b-2 ${
                  location.pathname === "/chat"
                    ? "text-foreground border-primary"
                    : "text-muted-foreground hover:text-foreground border-transparent hover:border-primary"
                }`}
              >
                Chat
              </Link>
            )}
            <Link
              to="/transparency"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`relative px-4 py-3 text-sm font-semibold transition-all duration-200 border-b-2 ${
                location.pathname === "/transparency"
                  ? "text-foreground border-primary"
                  : "text-muted-foreground hover:text-foreground border-transparent hover:border-primary"
              }`}
            >
              How It Works
            </Link>
          </nav>
          </div>
        </>
      )}

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />
    </header>
  );
}
